import React from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Chip,
  Button,
  Divider,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
} from '@mui/material';
import { ArrowBack, AccessTime, CalendarToday, Person, LinkedIn } from '@mui/icons-material';
import postsData from '../data/postsData';
import NewsletterSection from '../components/Layout/NewsletterSection';
import SEOHead from '../components/SEO/SEOHead';
import BreadcrumbNav from '../components/SEO/BreadcrumbNav';
import ShareButtons from '../components/shared/ShareButtons';
import FeedbackWidget from '../components/shared/FeedbackWidget';

const PostDetailPage = () => {
  const { slug, lng } = useParams();
  const post = postsData.find((p) => p.slug === slug);

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 2 }}>
          Articulo no encontrado
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Lo sentimos, el articulo que buscas no existe o fue removido.
        </Typography>
        <Button
          component={RouterLink}
          to={`/${lng}/blog`}
          variant="contained"
          sx={{ bgcolor: '#1E3A5F', borderRadius: '14px', textTransform: 'none', fontWeight: 600 }}
        >
          Volver a guías
        </Button>
      </Container>
    );
  }

  const relatedPosts = postsData.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <Box sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogImage={post.image}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: post.date,
          author: {
            '@type': 'Person',
            name: post.author?.name || post.author,
            url: post.author?.linkedin,
          },
          publisher: {
            '@type': 'Organization',
            name: 'FinMaple',
            url: 'https://www.finmaple.com',
            logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
          },
        }}
      />
      {/* Hero Image */}
      <Box
        role="img"
        aria-label={`Imagen de portada: ${post.title}`}
        sx={{
          width: '100%',
          height: { xs: 250, md: 420 },
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.45)), url(${post.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Container maxWidth="md" sx={{ pb: 4 }}>
          <Button
            component={RouterLink}
            to={`/${lng}/blog`}
            startIcon={<ArrowBack />}
            sx={{ color: '#fff', textTransform: 'none', mb: 2, fontWeight: 500 }}
          >
            Volver a guías
          </Button>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ mt: -4, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 4 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}
        >
          <BreadcrumbNav items={[{ label: 'Guías', to: `/${lng}/blog` }, { label: post.title }]} />
          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {post.tags.map((tag, i) => (
              <Chip
                key={i}
                label={tag}
                size="small"
                sx={{
                  bgcolor: '#E6E9EE',
                  color: '#1E3A5F',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              />
            ))}
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            {post.title}
          </Typography>

          {/* Meta */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              color: '#5F6B7A',
              mb: 3,
              fontSize: '0.9rem',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
              {post.author?.linkedin ? (
                <a
                  href={post.author.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F' }}>
                    {post.author.name}
                  </Typography>
                  <LinkedIn sx={{ fontSize: 16, color: '#1E3A5F' }} />
                </a>
              ) : (
                <Typography variant="body2">{typeof post.author === 'string' ? post.author : post.author?.name}</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                {new Date(post.date).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2">{post.readTime} min de lectura</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Share buttons */}
          <Box sx={{ mb: 3 }}>
            <ShareButtons title={post.title} />
          </Box>

          {/* Article Body */}
          <Box
            className="article-body"
            sx={{
              '& h2': {
                fontWeight: 600,
                fontSize: { xs: '1.3rem', md: '1.5rem' },
                color: '#1A1A1A',
                mt: 4,
                mb: 1.5,
              },
              '& h3': {
                fontWeight: 600,
                fontSize: { xs: '1.1rem', md: '1.2rem' },
                color: '#1E3A5F',
                mt: 3,
                mb: 1,
              },
              '& p': {
                fontSize: '1rem',
                lineHeight: 1.8,
                color: '#1A1A1A',
                mb: 2,
              },
              '& ul, & ol': {
                pl: 3,
                mb: 2,
                '& li': {
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: '#1A1A1A',
                  mb: 0.5,
                },
              },
              '& strong': {
                color: '#1A1A1A',
                fontWeight: 600,
              },
              '& em': {
                color: '#5F6B7A',
                display: 'block',
                borderLeft: '3px solid #1E3A5F',
                pl: 2,
                py: 1,
                mt: 3,
                bgcolor: '#F7F9FC',
                borderRadius: '0 6px 6px 0',
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                my: 3,
                fontSize: '0.9rem',
                '& th': {
                  bgcolor: '#1A1A1A',
                  color: '#fff',
                  fontWeight: 600,
                  p: 1.5,
                  textAlign: 'left',
                },
                '& td': {
                  p: 1.5,
                  borderBottom: '1px solid #E6E9EE',
                  color: '#1A1A1A',
                },
                '& tr:nth-of-type(even)': {
                  bgcolor: '#F7F9FC',
                },
              },
            }}
            dangerouslySetInnerHTML={{ __html: post.content?.replace(/href="\/financlopedia\//g, `href="/${lng}/financlopedia/`) }}
          />

          {/* Author Bio */}
          {post.author?.name && (
            <>
              <Divider sx={{ mt: 4, mb: 3 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2.5,
                  bgcolor: '#F7F9FC',
                  borderRadius: '10px',
                }}
              >
                {post.author.photo ? (
                  <Box
                    component="img"
                    src={post.author.photo}
                    alt={post.author.name}
                    loading="lazy"
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: '#1E3A5F',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '1.2rem',
                      flexShrink: 0,
                    }}
                  >
                    {post.author.name.split(' ').map((n) => n[0]).join('')}
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                    {post.author.name}
                  </Typography>
                  {post.author.role && (
                    <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
                      {post.author.role}
                    </Typography>
                  )}
                </Box>
                {post.author.linkedin && (
                  <Button
                    href={post.author.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<LinkedIn />}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      color: '#1E3A5F',
                      fontWeight: 600,
                      borderColor: '#1E3A5F',
                      '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' },
                    }}
                    variant="outlined"
                  >
                    LinkedIn
                  </Button>
                )}
              </Box>
            </>
          )}

          {/* Feedback */}
          <FeedbackWidget contentId={post.slug} />
        </Box>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <Box sx={{ mt: 6, mb: 4 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}
            >
              También te puede interesar
            </Typography>
            <Grid container spacing={3}>
              {relatedPosts.map((p) => (
                <Grid item xs={12} sm={4} key={p.id}>
                  <Card
                    sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      transition: 'box-shadow 0.3s',
                      '&:hover': { boxShadow: '0 6px 24px rgba(0,0,0,0.12)' },
                    }}
                  >
                    <CardActionArea component={RouterLink} to={`/${lng}${p.path || `/blog/${p.slug}`}`}>
                      <CardMedia
                        component="img"
                        height={160}
                        image={p.image}
                        alt={p.title}
                        loading="lazy"
                        sx={{ width: '100%', objectFit: 'cover' }}
                      />
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}
                        >
                          {p.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#5F6B7A', mt: 1, display: 'block' }}
                        >
                          {p.readTime} min de lectura
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default PostDetailPage;
